import React from 'react';
import {
  Action, ConfirmModal, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { Feedback } from '../../../providers/api/dtos';
import { FeedbackStatus } from '../../../domain/types';
import { updateFeedback } from '../../../providers/api/feedback/feedbackProvider';

interface ChildFnProps {
  archiveFeedback: VoidFunction,
  isLoading: boolean,
}

export interface ArchiveFeedbackActionProps extends React.AriaAttributes {
  feedback: Feedback,
  onSuccess?: (archivedFeedback: Feedback) => void,
  children?: (props: ChildFnProps) => React.ReactNode,
}

export const ARCHIVE_FEEDBACK_SUCCESS_MESSAGE = 'La survey è stata archiviata con successo.';
export const ARCHIVE_FEEDBACK_ERROR_MESSAGE = 'Non è stato possibile archiviare la survey.';

const ArchiveFeedbackAction: React.FC<ArchiveFeedbackActionProps> = ({
  feedback,
  onSuccess,
  children,
  ...rest
}) => {
  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const handleArchive = React.useCallback(async () => {
    try {
      setIsSaving(true);

      const feedbackToArchive: Feedback = {
        ...feedback,
        status: FeedbackStatus.ARCHIVED,
      };

      const archivedFeedback = await updateFeedback(feedbackToArchive.id!, feedbackToArchive);

      setIsSaving(false);
      addSuccess(ARCHIVE_FEEDBACK_SUCCESS_MESSAGE);
      modal.close();

      onSuccess?.(archivedFeedback);
    } catch (_) {
      setIsSaving(false);
      addError(ARCHIVE_FEEDBACK_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, feedback, modal, onSuccess]);

  if (feedback.status !== FeedbackStatus.TODO) {
    return null;
  }

  return (
    <>
      {children
        ? children({
          archiveFeedback: modal.open,
          isLoading: isSaving,
        })
        : (
          <Action
            label="Archivia"
            aria-label="Archivia la survey"
            size="S"
            onClick={() => modal.open()}
            {...rest}
          />
        )}
      <Portal>
        <ConfirmModal
          variant="critical"
          isOpen={modal.isOpen}
          saving={isSaving}
          aria-label="Conferma archiviazione survey"
          title="Conferma archiviazione survey"
          onConfirm={handleArchive}
          onAbort={() => modal.close()}
        >
          Conferma l&apos;operazione per archiviare la survey.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default ArchiveFeedbackAction;
